import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, MapPin } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import type { MatchInterviewEvent } from "./useMatchInterviewEvents";

interface InterviewPrepEventsProps {
	upcoming: MatchInterviewEvent[];
	past: MatchInterviewEvent[];
}

const formatEventDateTime = (iso: string): string => {
	try {
		return format(new Date(iso), "PPP · p");
	} catch {
		return iso;
	}
};

const EventRow = ({ event, isPast }: { event: MatchInterviewEvent; isPast: boolean }) => {
	const { t } = useTranslation();
	return (
		<div
			className={`flex items-start gap-3 p-3 rounded-sm border transition-colors ${
				isPast
					? "border-border bg-muted/40"
					: "border-primary/20 bg-primary/5"
			}`}
		>
			<div
				className={`p-2 rounded-sm shrink-0 ${
					isPast
						? "bg-muted text-muted-foreground"
						: "bg-primary/10 text-primary"
				}`}
			>
				<Calendar className="h-4 w-4" />
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="text-sm font-medium text-foreground truncate">
						{event.title}
					</span>
					{isPast && (
						<Badge variant="outline" className="text-xs">
							{t("matching.interviewPrep.events.past", "Past")}
						</Badge>
					)}
				</div>
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
					<span className="flex items-center gap-1">
						<Clock className="h-3 w-3" />
						{formatEventDateTime(event.start_time)}
					</span>
					{event.participantCount > 0 && (
						<span className="flex items-center gap-1">
							<Users className="h-3 w-3" />
							{event.participantCount}
						</span>
					)}
					{event.location && (
						<span className="flex items-center gap-1 truncate">
							<MapPin className="h-3 w-3" />
							{event.location}
						</span>
					)}
				</div>
			</div>
		</div>
	);
};

export const InterviewPrepEvents = ({ upcoming, past }: InterviewPrepEventsProps) => {
	const { t } = useTranslation();
	if (upcoming.length === 0 && past.length === 0) return null;

	return (
		<Card className="mb-6">
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
					<Calendar className="h-4 w-4 text-primary" />
					{t("matching.interviewPrep.events.title", "Linked interview events")}
				</CardTitle>
			</CardHeader>
			<CardContent className="pt-0 space-y-2">
				{upcoming.map((e) => (
					<EventRow key={e.id} event={e} isPast={false} />
				))}
				{past.map((e) => (
					<EventRow key={e.id} event={e} isPast />
				))}
			</CardContent>
		</Card>
	);
};
